/**
 * AI Router Logger - Visual logging of the AI routing pipeline
 */

import type { AIRoutingResult, PipelineStep, MessageAnalysis, RoutingDecision } from './types';

// Console styling
const STYLES = {
  header: 'background: linear-gradient(90deg, #667eea 0%, #764ba2 100%); color: white; padding: 8px 16px; border-radius: 4px; font-weight: bold; font-size: 14px;',
  subHeader: 'background: #374151; color: #f3f4f6; padding: 4px 8px; border-radius: 2px; font-weight: bold;',
  step: 'background: #1f2937; color: #60a5fa; padding: 2px 6px; border-radius: 2px;',
  success: 'color: #10b981; font-weight: bold;',
  warning: 'color: #f59e0b; font-weight: bold;',
  error: 'color: #ef4444; font-weight: bold;',
  info: 'color: #6b7280;',
  highlight: 'color: #8b5cf6; font-weight: bold;',
  provider: 'background: #059669; color: white; padding: 2px 8px; border-radius: 10px; font-weight: bold;',
  model: 'background: #2563eb; color: white; padding: 2px 8px; border-radius: 10px; font-weight: bold;',
  complexity: {
    trivial: 'background: #10b981; color: white; padding: 2px 8px; border-radius: 10px;',
    simple: 'background: #22c55e; color: white; padding: 2px 8px; border-radius: 10px;',
    moderate: 'background: #eab308; color: black; padding: 2px 8px; border-radius: 10px;',
    complex: 'background: #f97316; color: white; padding: 2px 8px; border-radius: 10px;',
    expert: 'background: #ef4444; color: white; padding: 2px 8px; border-radius: 10px;',
  },
};

/**
 * Log the complete AI routing pipeline
 */
export function logRoutingPipeline(result: AIRoutingResult): void {
  const { messageId, timestamp, originalMessage, analysis, decision, pipelineSteps } = result;

  console.log('\n');
  console.log('%c🤖 AI ROUTER PIPELINE', STYLES.header);
  console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #667eea');

  // Message info
  console.log('\n%c📨 INCOMING MESSAGE', STYLES.subHeader);
  console.log('%cID:%c ' + messageId, STYLES.info, '');
  console.log('%cTime:%c ' + timestamp.toISOString(), STYLES.info, '');
  console.log('%cMessage:%c "' + truncateMessage(originalMessage, 100) + '"', STYLES.info, 'color: #e5e7eb;');

  // Pipeline steps
  console.log('\n%c⚙️ PIPELINE STEPS', STYLES.subHeader);
  pipelineSteps.forEach((step) => {
    logPipelineStep(step);
  });

  // Analysis summary
  console.log('\n%c📊 MESSAGE ANALYSIS', STYLES.subHeader);
  logAnalysis(analysis);

  // Final decision
  console.log('\n%c🎯 ROUTING DECISION', STYLES.subHeader);
  logDecision(decision);

  // Visual summary
  console.log('\n%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #667eea');
  logVisualSummary(analysis, decision);
  console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #667eea');
  console.log('\n');
}

/**
 * Log a single pipeline step
 */
function logPipelineStep(step: PipelineStep): void {
  const icon = getStepIcon(step.name);
  console.groupCollapsed(
    `%c${step.step}%c ${icon} ${step.name} %c(${step.duration}ms)`,
    STYLES.step,
    '',
    STYLES.info
  );
  console.log('%cDescription:%c ' + step.description, STYLES.info, '');
  if (step.decision) {
    console.log('%c→ Decision:%c ' + step.decision, STYLES.highlight, '');
  }
  if (step.input !== undefined) {
    console.log('%cInput:', STYLES.info);
    console.log(step.input);
  }
  if (step.output !== undefined) {
    console.log('%cOutput:', STYLES.info);
    console.log(step.output);
  }
  console.groupEnd();
}

/**
 * Log message analysis
 */
function logAnalysis(analysis: MessageAnalysis): void {
  // Metrics table
  console.table({
    'Word Count': analysis.wordCount,
    'Character Count': analysis.charCount,
    'Sentence Count': analysis.sentenceCount,
    'Question Count': analysis.questionCount,
    'Estimated Tokens': analysis.estimatedTokens,
    'Has Code': analysis.hasCode ? '✓' : '✗',
    'Has Numbers': analysis.hasNumbers ? '✓' : '✗',
    'Has Technical Terms': analysis.hasTechnicalTerms ? '✓' : '✗',
    'Multiple Topics': analysis.hasMultipleTopics ? '✓' : '✗',
  });

  // Complexity
  const complexityStyle = STYLES.complexity[analysis.complexity];
  console.log(
    '%cComplexity:%c %c' + analysis.complexity.toUpperCase() + '%c (score: ' + analysis.complexityScore + '/100)',
    STYLES.info,
    '',
    complexityStyle,
    ''
  );

  // Complexity factors
  if (analysis.complexityFactors.length > 0) {
    console.log('%cFactors:%c ' + analysis.complexityFactors.join(', '), STYLES.info, '');
  }

  // Intent
  console.log('\n%cIntent:', STYLES.info);
  console.log('  Category: ' + analysis.intent.category);
  console.log('  Domain: ' + analysis.intent.domain);
  console.log('  Requires Context: ' + (analysis.intent.requiresContext ? 'Yes' : 'No'));
  console.log('  Requires Tools: ' + (analysis.intent.requiresTools ? 'Yes' : 'No'));
  console.log('  Expected Response: ' + analysis.intent.estimatedResponseLength);
}

/**
 * Log routing decision
 */
function logDecision(decision: RoutingDecision): void {
  console.log(
    '%cProvider:%c %c' + decision.provider.toUpperCase() + '%c',
    STYLES.info,
    '',
    STYLES.provider,
    ''
  );
  console.log(
    '%cModel:%c %c' + decision.model + '%c',
    STYLES.info,
    '',
    STYLES.model,
    ''
  );

  if (decision.fallbackProvider) {
    console.log(
      '%cFallback:%c ' + decision.fallbackProvider + ' / ' + decision.fallbackModel,
      STYLES.info,
      STYLES.warning
    );
  }

  console.log('%cConfidence:%c ' + (decision.confidence * 100).toFixed(0) + '%', STYLES.info, '');
  console.log('%cEst. Cost:%c ' + decision.estimatedCost, STYLES.info, '');
  console.log('%cEst. Latency:%c ' + decision.estimatedLatency, STYLES.info, '');

  // Reasoning
  console.log('\n%cReasoning:', STYLES.info);
  decision.reasoning.forEach((reason, i) => {
    console.log('  ' + (i + 1) + '. ' + reason);
  });
}

/**
 * Log visual summary
 */
function logVisualSummary(analysis: MessageAnalysis, decision: RoutingDecision): void {
  const arrow = '→';
  const complexityStyle = STYLES.complexity[analysis.complexity];

  console.log(
    '\n%c📝 Message %c' + arrow + '%c %c' + analysis.complexity.toUpperCase() + '%c %c' + arrow + '%c %c' + decision.provider.toUpperCase() + '%c %c' + arrow + '%c %c' + decision.model + '%c',
    'color: #9ca3af;',
    'color: #6b7280;',
    '',
    complexityStyle,
    '',
    'color: #6b7280;',
    '',
    STYLES.provider,
    '',
    'color: #6b7280;',
    '',
    STYLES.model,
    ''
  );
}

/**
 * Get icon for pipeline step
 */
function getStepIcon(stepName: string): string {
  const icons: Record<string, string> = {
    'receive': '📥',
    'tokenize': '🔤',
    'analyze': '🔍',
    'detect_intent': '🎯',
    'assess_complexity': '📊',
    'check_providers': '🔌',
    'select_provider': '✅',
    'select_model': '🤖',
    'finalize': '🏁',
  };
  return icons[stepName.toLowerCase()] || '•';
}

/**
 * Truncate message for display
 */
function truncateMessage(message: string, maxLength: number): string {
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength) + '...';
}

/**
 * Quick log for simple routing info
 */
export function logQuickRoute(message: string, provider: string, model: string, reason: string): void {
  console.log(
    '%c🤖 AI Route:%c "%s" %c→%c %c%s%c / %c%s%c (%s)',
    'color: #667eea; font-weight: bold;',
    'color: #9ca3af;',
    truncateMessage(message, 50),
    'color: #6b7280;',
    '',
    STYLES.provider,
    provider,
    '',
    STYLES.model,
    model,
    '',
    reason
  );
}

/**
 * Log routing error
 */
export function logRoutingError(message: string, error: Error): void {
  console.log('%c❌ AI Router Error', STYLES.error);
  console.log('%cMessage:%c ' + truncateMessage(message, 50), STYLES.info, '');
  console.error(error);
}
